import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import MinimalistLoginPage from './MinimalistLoginPage';
import ModernAdminDashboard from './ModernAdminDashboard';

interface MainLoginPageProps {
  t: any;
  isRTL: boolean;
}

const MainContainer = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 1.2rem;
  color: white;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
`;

const LoadingSpinner = styled(motion.div)`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const MainLoginPage: React.FC<MainLoginPageProps> = ({ t, isRTL }) => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
      if (user) {
        console.log('✅ User authenticated:', user.email);
      } else {
        console.log('ℹ️ No user authenticated');
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLoginSuccess = (user: any) => {
    setUser(user);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      console.log('✅ Logout successful');
    } catch (error) {
      console.error('❌ Logout error:', error);
    }
  };

  if (loading) {
    return (
      <LoadingContainer>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}
        >
          <LoadingSpinner />
          <div style={{ color: 'white', fontWeight: '600' }}>
            Loading...
          </div>
        </motion.div>
      </LoadingContainer>
    );
  }

  if (!user) {
    return <MinimalistLoginPage t={t} isRTL={isRTL} onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <MainContainer $isRTL={isRTL}>
      <ModernAdminDashboard t={t} isRTL={isRTL} onLogout={handleLogout} />
    </MainContainer>
  );
};

export default MainLoginPage;
